<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="420">
    <v-card class="app-dialog" :class="{ 'is-danger': danger }">
      <div class="dialog-icon">
        <v-icon size="26" :color="danger ? '#f87171' : '#64ffda'">{{ icon }}</v-icon>
      </div>

      <h2 class="dialog-title">{{ title }}</h2>
      <p class="dialog-text">{{ message }}</p>

      <div class="dialog-actions">
        <button type="button" class="ghost-btn" @click="cancel">{{ cancelText }}</button>
        <button type="button" :class="danger ? 'danger-btn' : 'primary-btn'" @click="confirm">
          {{ confirmText }}
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { defineEmits, defineProps } from 'vue';

defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: "Are you sure?" },
  message: { type: String, default: "" },
  confirmText: { type: String, default: "Confirm" },
  cancelText: { type: String, default: "Cancel" },
  // danger = destructive action (red, e.g. delete). Otherwise the app's teal accent.
  danger: { type: Boolean, default: false },
  icon: { type: String, default: "mdi-help-circle-outline" },
})

const emit = defineEmits(["update:modelValue", "confirm"])

const cancel = () => {
  emit("update:modelValue", false)
}

const confirm = () => {
  emit("confirm")
  emit("update:modelValue", false)
}
</script>

<style scoped>
.app-dialog {
  border-radius: 16px !important;
  background: var(--duallo-card-gradient) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5) !important;
  color: #fff;
  padding: 28px 24px 22px;
  text-align: center;
  user-select: none;
}

.dialog-icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 14px;
  border-radius: 14px;
  background: rgba(100, 255, 218, 0.1);
  border: 1px solid rgba(100, 255, 218, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-dialog.is-danger .dialog-icon {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.25);
}

.dialog-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 6px;
  color: #ffffff;
}

.dialog-text {
  font-size: 0.92rem;
  color: #b0bec5;
  margin: 0 0 22px;
  line-height: 1.5;
}

.dialog-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.ghost-btn,
.primary-btn,
.danger-btn {
  flex: 1 1 0;
  max-width: 160px;
  padding: 10px 18px;
  border-radius: 10px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.ghost-btn {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfd8dc;
}

.ghost-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}

.primary-btn {
  background: rgba(100, 255, 218, 0.12);
  border: 1px solid rgba(100, 255, 218, 0.4);
  color: var(--duallo-accent);
}

.primary-btn:hover {
  background: rgba(100, 255, 218, 0.22);
}

.danger-btn {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #f87171;
}

.danger-btn:hover {
  background: rgba(239, 68, 68, 0.25);
}
</style>
